import * as _ from 'lodash';
import { Model, Attrs } from '../../../shared/models/model.class';
import { Deal } from '../deal/deal.model';
import { Document } from '../document/document.model';

export class Request extends Model<Request> {

    private static statusLabels = {
        'created': 'Needed',
        'published': 'Requested',
        'completed': 'Collected',
        'delivered': 'Sent',
        'accepted': 'Accepted',
        'rejected': 'Rejected',
        'default': 'Needed'
    };

    // base
    id: number;
    borrowerId: number;
    dealId: number;
    deal: Deal;
    creatorUserId: number;
    type: string;
    publishTo: string;
    deliverTo: string;
    rejectedReason: string;
    rejectedReasonOther: string;
    created: Date;
    published: Date;
    completed: Date;
    delivered: Date;
    accepted: Date;
    rejected: Date;
    modified: Date;
    deleted: Date;
    createdTimestamp: number;
    modifiedTimestamp: number;
    publishedTimestamp: number;
    completedTimestamp: number;
    deliveredTimestamp: number;
    acceptedTimestamp: number;
    rejectedTimestamp: number;
    actions: Array<string>;

    // documentRequest
    categoryId: string;
    year: number;
    month: number;
    name: string;
    description: string;
    documentId: number;
    document: Document;

    // textRequest
    value: string;

    // computed
    nameWithDate?: string;
    displayStatus?: string;
    requiresAction: boolean;

    private _status: string;

    constructor(props?: Attrs) {
        super(props);

        this.nameWithDate = this.calcNameWithDate();
        this.requiresAction = ['created', 'published', 'accepted'].includes(this.status);
    }

    fillable() {
        return [
            'id',
            'borrowerId',
            'dealId',
            'deal',
            'creatorUserId',
            'type',
            'publishTo',
            'deliverTo',
            'status',
            'rejectedReason',
            'rejectedReasonOther',
            'created',
            'published',
            'completed',
            'delivered',
            'accepted',
            'rejected',
            'modified',
            'deleted',
            'createdTimestamp',
            'modifiedTimestamp',
            'publishedTimestamp',
            'completedTimestamp',
            'deliveredTimestamp',
            'acceptedTimestamp',
            'rejectedTimestamp',
            'actions',
            'categoryId',
            'year',
            'month',
            'name',
            'description',
            'documentId',
            'requirements',
            'localCreated',
            'value'
        ];
    }

    serializeable() {
        return this.fillable();
    }

    documentSatisfies (document: Document) {
        return (
            this.type === 'documentRequest'
            && document.borrowerId === this.borrowerId
            && document.category === this.categoryId
            && this.documentSatisfiesTimeClassification(document)
        );
    }

    documentSatisfiesTimeClassification (document: Document) {
        if (!this.month && !this.year) {
            return true;
        }

        if (this.month) {
            return document.months.includes(this.monthString);
        }

        if (this.year) {
            return document.years.includes(this.yearString);
        }

        return false;
    }

    get sortableYearMonth() {
        if (this.month) {
            return this.year + '-' + String(this.month).padStart(2, '0');
        }

        if (this.year) {
            return String(this.year);
        }

        return '';
    }

    get documentBlueprint() {
        return {
            borrowerId: this.borrowerId,
            category: this.categoryId,
            years: this.timeClassification === 'year' ? [ this.yearString ] : [],
            months: this.timeClassification === 'month' ? [ this.monthString ] : []
        };
    }

    get timeClassification() {
        if (this.month) {
            return 'month';
        }

        if (this.year) {
            return 'year';
        }

        return null;
    }

    get monthString() {
        return this.month.toString().padStart(2, '0') + '/' + this.year;
    }

    get yearString() {
        return this.year.toString();
    }

    get timeClassificationString() {
        if (this.month) {
            return this.monthString;
        }

        if (this.year) {
            return this.yearString;
        }

        return null;
    }

    get status () {
        return this._status;
    }

    set status (newValue) {
        this._status = newValue;
        this.displayStatus = this.requestStatusToLabel(newValue);
    }

    calcNameWithDate() {
        if (this.month) {
            const date = new Date(this.year, this.month - 1, 1);
            return this.name + ' - ' + date.toLocaleString('en-us', {month: 'long'}) + ' ' + date.getFullYear();
        }

        if (this.year) {
            const date = new Date(this.year, 0, 1);
            return this.name + ' - ' + date.getFullYear();
        }

        return this.name;
    }

    requestStatusToLabel (status) {
        return Request.statusLabels[status] || _.capitalize(status);
    }
}
